import React, {useContext, useEffect, useState} from "react";
import {Alert, Button, Col, Row} from "react-bootstrap";
import AuthContext from "../../Contexts/authContext";
import {
    retrieveSubscriptionManage,
    disableRenewal,
    enableRenewal,
    previewChangePlan,
    confirmChangePlan,
    updatePaymentMethod,
    cancelSubscription
} from "../../Api/Subscriptions/SubscriptionFetch";
import SubscriptionStatusBadge from "../../Components/Subscription/SubscriptionStatusBadge";
import ModalChangePlanPreview from "../../Components/Modals/Subscription/ModalChangePlanPreview";
import ModalCancelSubscription from "../../Components/Modals/Subscription/ModalCancelSubscription";
import {ConfirmationModal} from "../../Components/Modals/ConfirmationModal";
import {formatDataIt} from "../../Utils/DateFormat";
import Loading from "../../Components/Layout/Elements/Loading";

const ManageSubscription = () => {
    const {userProfile} = useContext(AuthContext);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [errors, setErrors] = useState(null);
    const [update, setUpdate] = useState(false);

    // Modal states
    const [showDisableRenewal, setShowDisableRenewal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [selectedPlanCode, setSelectedPlanCode] = useState(null);
    const [showCancel, setShowCancel] = useState(false);

    useEffect(() => {
        if (userProfile) {
            retrieveSubscriptionManage({setSubscriptionData, setIsLoading});
        }
    }, [userProfile, update]);

    const handleDisableRenewal = () => {
        setShowDisableRenewal(false);
        disableRenewal({
            subscriptionId: subscriptionData.subscription.id,
            setMessage, setErrors, setUpdate, setIsLoading
        });
    };

    const handleEnableRenewal = () => {
        enableRenewal({
            subscriptionId: subscriptionData.subscription.id,
            setMessage, setErrors, setUpdate, setIsLoading
        });
    };

    const handlePreviewChangePlan = (planCode) => {
        setSelectedPlanCode(planCode);
        previewChangePlan({
            subscriptionId: subscriptionData.subscription.id,
            planCode,
            setPreviewData, setShowPreview, setErrors, setIsLoading
        });
    };

    const handleConfirmChangePlan = () => {
        setShowPreview(false);
        confirmChangePlan({
            subscriptionId: subscriptionData.subscription.id,
            planCode: selectedPlanCode,
            setMessage, setErrors, setUpdate, setIsLoading
        });
    };

    const handleUpdatePaymentMethod = () => {
        updatePaymentMethod({
            subscriptionId: subscriptionData.subscription.id,
            setErrors, setIsLoading
        });
    };

    const handleCancelSubscription = (reason) => {
        setShowCancel(false);
        cancelSubscription({
            subscriptionId: subscriptionData.subscription.id,
            reason,
            setMessage, setErrors, setUpdate, setIsLoading
        });
    };

    if (!subscriptionData) {
        return isLoading ? <Loading/> : null;
    }

    const {subscription, plan, next_charge, payment_method, available_plans, cancel_info} = subscriptionData;
    const isBlocked = ['PAYMENT_FAILED', 'SUSPENDED'].includes(subscription.status);

    return (
        <>
            <Row className="mb-4">
                <Col>
                    <h2 className="text-start">Gestisci Abbonamento</h2>
                </Col>
            </Row>

            {message && (
                <Alert variant="success" onClick={() => setMessage(null)} dismissible>
                    {message}
                </Alert>
            )}
            {errors && (
                <Alert variant="danger" onClick={() => setErrors(null)} dismissible>
                    {errors}
                </Alert>
            )}

            {/* Avviso pagamento fallito */}
            {isBlocked && (
                <Alert variant="danger" className="d-flex justify-content-between align-items-center">
                    <div>
                        <strong>C'è un problema con il tuo pagamento.</strong>
                        <p className="mb-0 mt-1">Aggiorna il metodo di pagamento per riattivare il tuo account.</p>
                    </div>
                    {subscriptionData.can_update_payment_method && (
                        <Button className="bg-danger border-0 fw-bold" onClick={handleUpdatePaymentMethod}>
                            Aggiorna metodo di pagamento
                        </Button>
                    )}
                </Alert>
            )}

            {/* Card piano attuale */}
            <Row className="mb-4">
                <Col>
                    <div className="card shadow border-0 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="mb-0">Piano {plan.name}</h4>
                            <SubscriptionStatusBadge status={subscription.status}/>
                        </div>

                        <Row>
                            <Col md={6}>
                                {subscription.is_trial && (
                                    <>
                                        <p className="mb-1">
                                            <strong>Fine prova gratuita:</strong> {formatDataIt(new Date(subscription.end_date))}
                                        </p>
                                        {next_charge && (
                                            <>
                                                <p className="mb-1">
                                                    <strong>Primo addebito:</strong> €{next_charge.amount?.toFixed(2)}
                                                </p>
                                                <p className="mb-1">
                                                    <strong>Data primo addebito:</strong> {formatDataIt(new Date(next_charge.date))}
                                                </p>
                                            </>
                                        )}
                                    </>
                                )}

                                {!subscription.is_trial && subscription.status !== 'BASIC_ACTIVE' && (
                                    <>
                                        {next_charge && (
                                            <>
                                                <p className="mb-1">
                                                    <strong>Prossimo rinnovo:</strong> {formatDataIt(new Date(next_charge.date))}
                                                </p>
                                                <p className="mb-1">
                                                    <strong>Importo:</strong> €{next_charge.amount?.toFixed(2)}/anno
                                                </p>
                                            </>
                                        )}
                                        {subscription.last_payment && (
                                            <p className="mb-1">
                                                <strong>Ultimo pagamento:</strong> {formatDataIt(new Date(subscription.last_payment))}
                                            </p>
                                        )}
                                    </>
                                )}

                                {payment_method && (
                                    <p className="mb-1">
                                        <strong>Metodo di pagamento:</strong> {payment_method.type} {payment_method.masked}
                                    </p>
                                )}
                            </Col>

                            <Col md={6} className="text-md-end mt-3 mt-md-0">
                                {subscriptionData.can_disable_renewal && (
                                    <Button
                                        className="bg-warning border-0 text-dark fw-bold me-2"
                                        onClick={() => setShowDisableRenewal(true)}
                                    >
                                        Disattiva rinnovo
                                    </Button>
                                )}
                                {subscriptionData.can_enable_renewal && (
                                    <Button
                                        className="bg-success border-0 text-white fw-bold me-2"
                                        onClick={handleEnableRenewal}
                                    >
                                        Riattiva rinnovo
                                    </Button>
                                )}
                                {subscriptionData.can_update_payment_method && (
                                    <Button
                                        className="bkg-second border-0 fw-bold"
                                        onClick={handleUpdatePaymentMethod}
                                    >
                                        Aggiorna metodo di pagamento
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            {/* Sezione cambio piano */}
            <Row className="mb-4">
                <Col>
                    <h4 className="mb-3">Cambia piano</h4>
                </Col>
            </Row>
            <Row className="mb-4">
                {available_plans && available_plans.map((p) => (
                    <Col md={4} key={p.code} className="mb-3">
                        <div className={`card shadow border-0 p-4 h-100 text-center ${p.is_current ? 'border-2 border-primary' : ''}`}>
                            <h5 className="fw-bold">{p.name}</h5>
                            <p className="fs-4 fw-bold mb-3">
                                {p.price > 0 ? `€${p.price.toFixed(2)}/anno` : 'Gratuito'}
                            </p>
                            {p.is_current ? (
                                <span className="badge bg-primary py-2 px-3 mx-auto" style={{fontSize: '14px'}}>
                                    Piano attuale
                                </span>
                            ) : (
                                <Button
                                    className="bkg-second border-0 fw-bold"
                                    onClick={() => handlePreviewChangePlan(p.code)}
                                >
                                    Passa a {p.name}
                                </Button>
                            )}
                        </div>
                    </Col>
                ))}
            </Row>

            {/* Footer */}
            <Row className="mt-4 mb-5">
                <Col className="text-center">
                    <a href="mailto:info@appmyteam.it" className="text-decoration-none">
                        Contatta l'assistenza
                    </a>
                </Col>
            </Row>

            {/* Modali */}
            <ConfirmationModal
                show={showDisableRenewal}
                handleClose={() => setShowDisableRenewal(false)}
                handleConfirm={handleDisableRenewal}
                actionMessage="Sei sicuro di voler disattivare il rinnovo automatico?"
                content="Il piano resterà attivo fino alla scadenza, poi passerà a BASIC."
            />

            <ModalChangePlanPreview
                show={showPreview}
                handleClose={() => setShowPreview(false)}
                handleConfirm={handleConfirmChangePlan}
                previewData={previewData}
                isLoading={isLoading}
            />

            <ModalCancelSubscription
                show={showCancel}
                handleClose={() => setShowCancel(false)}
                handleConfirm={handleCancelSubscription}
                cancelInfo={cancel_info}
                isLoading={isLoading}
            />

            {isLoading && <Loading/>}
        </>
    );
};

export default ManageSubscription;
